/**
 * RouteBuilder — the /routeBuilder page. Building is staged coarse-to-fine, and the stage is read off the map's zoom
 * rather than remembered: zoomed out to the city, streets are too dense to aim at, so the regions are shown as a
 * tinted, labeled overview and a click zooms in to the one under it; zoomed in (however the user got there — that
 * click, the scroll wheel, or an address search), a click drops the route's start, previewed by a ghost flag. Each
 * further click extends the route from the last point along an A* walking path over the street network (RouteGraph),
 * anywhere in the city: regions organize missions, not routes, so a route is free to cross from one into the next
 * (#3488). Routes are contiguous by construction. Clicking the drawn route opens a small action menu (RoutePopover:
 * reverse / delete). A slim Start/End address seed (DirectionsPanel) can plant the first two points.
 * The save flow lives in SaveModal; saved routes surface as cards on the intro panel (SavedRoutesPanel). Leaving with
 * unsaved work is caught by UnsavedChangesGuard; only the sign-in round trip carries a route across a page load.
 *
 * The route is defined entirely by #waypoints (ordered snapped points); the drawn streets, endpoint flags, and
 * stats are all derived from them by #recompute, so add / undo / reverse are just edits to that list followed by a
 * recompute.
 */

import MapboxLanguage from '@mapbox/mapbox-gl-language';
import mapboxgl from '../common/mapboxgl.js';
import * as turf from '@turf/turf';
import i18next from 'i18next';
import { ConfirmDialog } from '../common/ConfirmDialog.js';
import { KeyboardShortcuts } from '../common/KeyboardShortcuts.js';
import { Toast } from '../common/Toast.js';
import { UnsavedChangesGuard } from '../common/UnsavedChangesGuard.js';
import { util } from '../common/utilities.js';
import { DirectionsPanel } from './DirectionsPanel.js';
import { RouteGraph } from './RouteGraph.js';
import { RoutePopover } from './RoutePopover.js';
import { SaveModal } from './SaveModal.js';
import { SavedRoutesPanel } from './SavedRoutesPanel.js';
import { UndoStack } from './UndoStack.js';

export class RouteBuilder {
  /**
   * Resolves a design-system color token for Mapbox paint, which takes literal colors rather than CSS variables.
   * Same approach as ps-map's addStreetsToMap, so a token retune reaches every map in the app.
   *
   * @param {string} name - The CSS custom property, e.g. '--color-asphalt-500'.
   * @returns {string}
   */
  static #token(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  // Zoom eased to when the first point lands, close enough that the basemap renders street names for building.
  static BUILD_ZOOM = 15.5;
  // The zoom at which a click starts placing points instead of zooming in. At 14 a city block is about ten pixels
  // long: the closest view where the ghost flag can still be steered onto one particular intersection.
  static PLACE_ZOOM = 14;
  // How far a clicked or geocoded point may be from a street and still snap to it. Generous enough for a click
  // aimed at a street or an address set back from one, tight enough that a point out in the water or past the
  // city's edge doesn't silently attach to a street the user never pointed at.
  static MAX_SNAP_DISTANCE_M = 250;
  // How long the explorer takes to walk the route in the preview animation.
  static EXPLORER_ANIMATION_MS = 2500;
  // How long the pointer rests on the drawn route before its action menu opens (discoverable but not twitchy).
  static ROUTE_MENU_HOVER_MS = 500;
  // How long the co-located next-step hint stays anchored to the newest flag.
  static HINT_DURATION_MS = 7000;

  // Muted categorical tints cycled by region id, so adjacent regions read as visually distinct choices.
  static REGION_FILL_COLORS = ['#78C9AB', '#FBD98C', '#F29173', '#9F9DB1', '#78B0EA'];

  // Region paints by stage: in the overview every region is washed and outlined so the map reads as a set of
  // places to zoom in to; while building, the wash goes and only faint outlines stay, as orientation. They mark
  // where one neighborhood ends, not where a route must.
  static REGION_PAINT_OVERVIEW = {
    fillOpacity: ['case', ['boolean', ['feature-state', 'hover'], false], 0.38, 0.18],
    lineOpacity: 0.4,
  };

  static REGION_PAINT_BUILDING = {
    fillOpacity: 0,
    lineOpacity: 0.25,
  };

  #status = {
    regionsRendered: false, // Region source/layers have been added to the map.
    streetsRendered: false, // Street source/layers/handlers have been added to the map.
    pendingRouteRestored: false,
  };

  #minutesPer100m;

  #mapboxApiKey;
  #map;
  #mapLoaded; // Resolves on the map's first 'load'; regions and streets are drawn only after it, whenever they arrive.
  #cityView; // The city's default {center, zoom}, for resetting the camera when starting over.
  #isSignedIn;

  // Route state. The route is #waypoints plus the resolved street list of each leg between them (#segments);
  // the drawn streets, endpoint flags, and stats are derived from those.
  #regionData = null;
  #overviewShown = null; // Which stage the region layers are painted for (null until they exist). See #syncStage.
  #hoveredRegion = null; // Region id carrying the overview's hover feature-state, or null.
  #streetData = null;
  #streetsInRoute = null; // The 'streets-chosen' GeoJSON source: cloned, oriented street features for the route.
  #waypoints = []; // Ordered [{ lng, lat }] snapped points the user clicked/seeded.
  #segments = []; // Per leg (waypoint i -> i+1), the resolved [{ streetId, flip }] in walking order.
  #loadSeq = 0; // Saved-route load counter, so only the newest load may draw (see #loadRouteForEditing).
  #chosenIds = new Set(); // Street ids currently drawn (their 'chosen' feature-state hides the base street).
  #endpointData = { type: 'FeatureCollection', features: [] }; // The 'route-endpoints' symbol source (flags).
  #routeGraph = null; // Built from #streetData as soon as it arrives (the ghost flag needs it pre-click).
  #ghostRaf = null; // Pending requestAnimationFrame id for the ghost-flag update.
  #ghostLngLat = null; // Latest mouse position awaiting a ghost-flag update.
  #explorerRaf = null; // Pending requestAnimationFrame id for the save-hover explorer animation.
  #hintPopup = null; // The next-step hint anchored at the newest flag (null when not showing).
  #hintTimeout = null; // Auto-hide timer for #hintPopup.
  #cursorGuide = null; // The pointer-following what-a-click-does bubble (created lazily).
  #editingRouteId = null; // The saved route loaded in the editor (null while building a brand-new route).
  #savedBaseline = null; // JSON of #routeStreetsPayload() at the last save/load; differing payload = unsaved edits.
  #pendingLeaveSave = null; // Resolver for a leave prompt's "save first", settled when the save modal closes.
  #signingIn = false; // The sign-in round-trip stashes the route and comes back to it, so it isn't a loss.

  // Collaborators.
  #saveModal;
  #savedRoutes;
  #undoStack;
  #directionsPanel;
  #routePopover;

  // The on-map call-to-action for the pre-route stages, as a shared Toast held open for as long as its stage lasts.
  #cta = null;
  #ctaKey = null; // Message key of the stage the toast is showing (or was closed for), null once the route starts.
  #ctaDismissed = new Set(); // Stage keys the user closed with the X, so returning to that stage doesn't re-raise it.

  // DOM elements.
  #panel;
  #deleteRouteModal;
  #streetDistanceEl;
  #routeTimeEl;
  #routeTimeTipEl;
  #statsCaptionEl;
  #routeStatsEl;
  #saveButton;
  #previewButton;
  #defaultSaveLabel;
  #deleteTitleEl;
  #deleteExplanationEl;
  #deleteConfirmButton;
  #defaultDeleteCopy;

  /**
   * @param {string} mapboxApiKey
   * @param {Record<string, any>} mapParams - City center/boundaries/zoom for initializing the map.
   * @param {boolean} isSignedIn - Whether the user is signed in (vs anonymous), from the server.
   * @param {number} minutesPer100m - The city's labeling pace (minutes per 100 m), for the exploration-time
   *   estimate; server-provided (ConfigService.getCityLabelingSpeed).
   */
  constructor(mapboxApiKey, mapParams, isSignedIn, minutesPer100m) {
    this.#mapboxApiKey = mapboxApiKey;
    this.#isSignedIn = isSignedIn === true;
    this.#minutesPer100m = minutesPer100m;

    // Get the DOM elements.
    this.#panel = document.getElementById('routebuilder-panel');
    this.#deleteRouteModal = document.getElementById('delete-route-modal-backdrop');
    this.#streetDistanceEl = document.getElementById('route-length-val');
    this.#routeTimeEl = document.getElementById('route-time-val');
    this.#routeTimeTipEl = document.getElementById('route-time-tip');
    this.#statsCaptionEl = document.getElementById('route-stats-caption');
    this.#routeStatsEl = document.getElementById('route-stats');
    this.#saveButton = document.getElementById('save-button');
    this.#previewButton = document.getElementById('preview-button');
    this.#defaultSaveLabel = this.#saveButton.textContent;
    this.#deleteTitleEl = document.getElementById('delete-route-title');
    this.#deleteExplanationEl = document.getElementById('delete-route-explanation');
    this.#deleteConfirmButton = document.getElementById('delete-route-button');
    // The confirm modal's server-rendered copy is for the brand-new-route case; editing swaps it (and back).
    this.#defaultDeleteCopy = {
      title: this.#deleteTitleEl.textContent,
      explanation: this.#deleteExplanationEl.textContent,
      button: this.#deleteConfirmButton.textContent,
    };

    // Wire the route-management buttons.
    document.getElementById('cancel-button').addEventListener('click', () => this.#clickCancelRoute());
    // Clears everything so the user can start a fresh route (confirming first if unsaved work would be lost),
    // zooming back out to the city view so they can pick where the next one goes.
    document.getElementById('new-route-button').addEventListener('click', async () => {
      window.logWebpageActivity('RouteBuilder_Click=NewRoute');
      if (!(await this.#unsavedWorkConfirmed())) return;
      this.#exitEditSession();
      this.#map.flyTo({ center: this.#cityView.center, zoom: this.#cityView.zoom, duration: 1200 });
    });
    document.getElementById('delete-route-button').addEventListener('click', () => this.#clearRoute());
    document.getElementById('cancel-delete-route-button').addEventListener('click', () => this.#clickResumeRoute());
    this.#initEstTimeTooltip();

    this.#saveModal = new SaveModal({
      isSignedIn: this.#isSignedIn,
      getStreetsPayload: () => this.#routeStreetsPayload(),
      getSuggestedName: () => this.#suggestedRouteName(),
      getCamera: () => this.#cameraSnapshot(),
      onSaved: (saved) => this.#handleRouteSaved(saved),
      onClose: () => {
        this.#settleLeaveSave(false);
        this.#saveButton.focus();
      },
      onSignIn: () => {
        this.#signingIn = true;
      },
    });
    new UnsavedChangesGuard({
      isDirty: () => !this.#signingIn && this.#hasUnsavedWork(),
      save: () => this.#saveForLeaving(),
      onChoice: (choice) => window.logWebpageActivity(`RouteBuilder_Click=UnsavedLeave_${choice}`),
    });
    this.#savedRoutes = new SavedRoutesPanel({
      isSignedIn: this.#isSignedIn,
      formatMeta: (distanceMeters, regionName) => this.#formatRouteMeta(distanceMeters, regionName),
      onView: (routeId) => this.#loadRouteForEditing(routeId),
    });
    this.#savedRoutes.refresh();

    this.#undoStack = new UndoStack(/** @type {HTMLButtonElement} */ (document.getElementById('undo-button')));
    document.getElementById('undo-button').addEventListener('click', () => {
      window.logWebpageActivity('RouteBuilder_Click=Undo');
      this.#undo();
    });
    document.addEventListener('keydown', (e) => {
      // Ctrl/Cmd+Z undoes the last route edit, except while typing in a form field, or while a dialog is open: the
      // save dialog would then store the route as it was cut back behind it.
      const { tagName } = /** @type {Element} */ (e.target);
      const dialogOpen = this.#saveModal.isOpen() || this.#deleteRouteModal.style.visibility === 'visible';
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && KeyboardShortcuts.keyOf(e) === 'KeyZ'
        && !/^(input|textarea|select)$/i.test(tagName) && !dialogOpen) {
        e.preventDefault();
        window.logWebpageActivity('RouteBuilder_KeyboardShortcut=Undo');
        this.#undo();
      }
    });

    // Initialize the map.
    mapboxgl.accessToken = mapboxApiKey;
    this.#cityView = { center: [mapParams.city_center.lng, mapParams.city_center.lat], zoom: mapParams.default_zoom };
    this.#map = new mapboxgl.Map({
      container: 'routebuilder-map',
      style: 'mapbox://styles/projectsidewalk/cloov4big002801rc0qw75w5g?optimize=true',
      center: [mapParams.city_center.lng, mapParams.city_center.lat],
      zoom: mapParams.default_zoom,
      minZoom: 8.25,
      maxZoom: 19,
      maxBounds: [
        [mapParams.southwest_boundary.lng, mapParams.southwest_boundary.lat],
        [mapParams.northeast_boundary.lng, mapParams.northeast_boundary.lat],
      ],
      doubleClickZoom: false,
    });
    this.#map.addControl(new MapboxLanguage({ defaultLanguage: i18next.t('common:mapbox-language-code') }));
    this.#map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
    this.#mapLoaded = new Promise((resolve) => this.#map.once('load', resolve));

    // Once all the layers have loaded, put them in the correct order.
    this.#map.on('sourcedataloading', this.#moveLayers);
    // The pre-route stage is a function of zoom, so every zoom change may be a stage change.
    this.#map.on('zoom', () => this.#syncStage());

    this.#directionsPanel = new DirectionsPanel({
      map: this.#map,
      mapboxApiKey: this.#mapboxApiKey,
      bbox: [
        [mapParams.southwest_boundary.lng, mapParams.southwest_boundary.lat],
        [mapParams.northeast_boundary.lng, mapParams.northeast_boundary.lat],
      ],
      onSetStart: (lngLat) => this.#setStartFromAddress(lngLat),
      onSetEnd: (lngLat) => this.#addWaypoint(lngLat, 'AddressEnd'),
    });
    this.#routePopover = new RoutePopover(this.#map, () => this.#reverseRoute(), () => this.#openDeleteConfirm());

    // A brand-new route saves through the name modal; a loaded saved route updates in place, Word-style.
    this.#saveButton.addEventListener('click', () => {
      if (this.#editingRouteId !== null) {
        this.#updateSavedRoute();
      } else {
        this.#saveModal.open();
      }
    });
    // A playful preview: the explorer walks the route from start to end.
    this.#previewButton.addEventListener('click', () => {
      window.logWebpageActivity('RouteBuilder_Click=PreviewRoute');
      this.#animateExplorer();
    });

    document.getElementById('poi-toggle-checkbox')?.addEventListener('change', (e) => {
      const { checked } = /** @type {HTMLInputElement} */ (e.target);
      window.logWebpageActivity(`RouteBuilder_Click=TogglePois_Visible=${checked}`);
      this.#setPoiVisibility(checked);
    });

    this.#updateCta();
  }

  /**
   * Shows or hides the basemap's point-of-interest labels (schools, parks, libraries, ...). POIs help route
   * builders aim for destinations that matter to pedestrians, but can clutter dense areas, so the legend offers a
   * toggle.
   *
   * @param {boolean} visible
   */
  #setPoiVisibility(visible) {
    (this.#map.getStyle()?.layers ?? [])
      .filter((layer) => layer.type === 'symbol' && layer.id.includes('poi'))
      .forEach((layer) => this.#map.setLayoutProperty(layer.id, 'visibility', visible ? 'visible' : 'none'));
  }

  // Arrow field so the reference stays stable for the map on/off pair. Stacks the layers bottom-to-top.
  #moveLayers = () => {
    const map = this.#map;
    if (map.getLayer('regions-outline') && map.getLayer('streets') && map.getLayer('streets-chosen')) {
      // The flag/explorer layers may not exist yet (their icons rasterize async); they are then added on top
      // anyway, so only the base layers gate the reordering.
      ['regions-fill', 'regions-outline', 'streets', 'streets-chosen', 'regions-label',
        'ghost-start', 'route-endpoints', 'route-explorer']
        .filter((id) => map.getLayer(id))
        .forEach((id) => map.moveLayer(id));
      map.off('sourcedataloading', this.#moveLayers); // Remove the listener so we only do this once.
    }
  };

  /*
   * Function definitions.
   */

  /**
   * Wires the estimate's info tooltip to show on hover, keyboard focus, and click/tap, dismissing on mouse-out,
   * blur, Escape, or an outside click. A native `title` (the prior affordance) never opens on click and isn't
   * reachable by keyboard or touch; this follows the ARIA tooltip pattern (focusable trigger + aria-describedby).
   */
  #initEstTimeTooltip() {
    const val = this.#routeTimeEl;
    const tip = this.#routeTimeTipEl;
    if (!val || !tip) return;
    const show = () => {
      tip.hidden = false;
    };
    const hide = () => {
      tip.hidden = true;
    };
    val.addEventListener('mouseenter', show);
    val.addEventListener('mouseleave', hide);
    val.addEventListener('focus', show);
    val.addEventListener('blur', hide);
    val.addEventListener('click', show);
    val.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        hide();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        show();
      }
    });
    // A tap/click outside the estimate closes a tip opened by tap, where there's no mouse-out to dismiss it.
    document.addEventListener('click', (e) => {
      if (!val.contains(e.target)) hide();
    });
  }

  // A transient message floated over the map (no-street / no-path feedback).
  #showMapMessage(message) {
    Toast.show({ message, duration: 2500 });
  }

  /**
   * Switches the left planner card between its two states (CSS shows/hides the matching sections).
   * @param {'empty'|'building'} state - 'empty' shows the intro; 'building' shows the length + route actions.
   */
  #setPanelState(state) {
    this.#panel.dataset.state = state;
  }

  /**
   * Whether the user has placed (or restored) at least the route's start point.
   * @returns {boolean}
   */
  #routeStarted() {
    return this.#waypoints.length > 0;
  }

  /**
   * Whether the route has any streets yet; a lone start point has none.
   * @returns {boolean}
   */
  #routeDrawn() {
    return (this.#streetsInRoute?.features.length ?? 0) > 0;
  }

  /**
   * Whether the map is in the overview stage: no route yet, and zoomed out too far to aim at a street. Here the
   * regions are the click targets and a click zooms in; past PLACE_ZOOM (or once a route exists, at any zoom) clicks
   * place points.
   *
   * Deriving the stage from the zoom, rather than remembering that a region was clicked, is what lets every way of
   * getting close — a region click, the scroll wheel, an address search, a restored camera — lead to the same place.
   *
   * @returns {boolean}
   */
  #inOverview() {
    return !this.#routeStarted() && this.#map.getZoom() < RouteBuilder.PLACE_ZOOM;
  }

  /**
   * Brings the region layers, the CTA, and the pointer feedback in line with the current stage. Cheap to call on
   * every zoom event: the map is only touched when the stage actually flips.
   */
  #syncStage() {
    const map = this.#map;
    if (!this.#status.regionsRendered) return;
    const overview = this.#inOverview();
    if (overview === this.#overviewShown) return;
    this.#overviewShown = overview;

    const paint = overview ? RouteBuilder.REGION_PAINT_OVERVIEW : RouteBuilder.REGION_PAINT_BUILDING;
    map.setLayoutProperty('regions-label', 'visibility', overview ? 'visible' : 'none');
    map.setPaintProperty('regions-fill', 'fill-opacity', paint.fillOpacity);
    map.setPaintProperty('regions-outline', 'line-opacity', paint.lineOpacity);
    // What the pointer was showing belongs to the stage just left; the next mouse move redraws it for this one.
    this.#clearRegionHover();
    this.#clearGhostStart();
    this.#setCursorGuide(null);
    map.getCanvas().style.cursor = '';
    this.#updateCta();
  }

  /**
   * Drops the overview's hover tint. The hover handler stops listening outside the overview, so a tint left on
   * when the stage flips would otherwise reappear, stale, on the next zoom back out.
   */
  #clearRegionHover() {
    if (this.#hoveredRegion === null) return;
    this.#map.setFeatureState({ source: 'regions', id: this.#hoveredRegion }, { hover: false });
    this.#hoveredRegion = null;
  }

  /**
   * Keeps the on-map call-to-action in step with the pre-route stages: zoom in, then pick a start point. It is the
   * shared Toast, so it looks and closes like every other notice, but held open rather than timed out: it describes
   * what the next click does for as long as that stays true. A stage the user closed stays closed if they wander
   * back to it (the X means "I know", not "not now"). Once the route has started, guidance moves to the hint
   * anchored at the newest flag (#showHint) and the toast goes.
   */
  #updateCta() {
    if (!this.#map) return;
    let key = null;
    if (!this.#routeStarted()) key = this.#inOverview() ? 'cta-select-region' : 'cta-pick-start';
    if (key === this.#ctaKey) return;
    this.#cta?.dismiss();
    this.#cta = null;
    this.#ctaKey = key;
    if (key === null || this.#ctaDismissed.has(key)) return;
    const stage = key === 'cta-select-region' ? 'SelectRegion' : 'PickStart';
    // The title slot, not the message: this is the page's one instruction, not an aside, and it sits over a map
    // whose own labels are set in bold. Its top edge lines up with the planner card's, read from the card's own
    // style so the two can't drift apart.
    this.#cta = Toast.show({
      title: i18next.t(key),
      reference: this.#map.getContainer(),
      top: parseFloat(getComputedStyle(this.#panel).top),
      duration: 0,
      onClose: () => {
        this.#ctaDismissed.add(key);
        window.logWebpageActivity(`RouteBuilder_Click=DismissCta_Stage=${stage}`);
      },
    });
  }

  /**
   * Shows a transient next-step hint anchored beside a route point, so guidance sits where the user is already
   * looking instead of in the far-away top-center pill.
   *
   * @param {[number, number]} coord - [lng, lat] to anchor at (the newest flag).
   * @param {string} text
   */
  #showHint(coord, text) {
    this.#hideHint();
    this.#hintPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'left',
      offset: 18,
      maxWidth: '260px',
      className: 'rb-map-hint',
    }).setLngLat(coord).setText(text).addTo(this.#map);
    this.#hintTimeout = setTimeout(() => this.#hideHint(), RouteBuilder.HINT_DURATION_MS);
  }

  /** Removes the next-step hint (if showing). */
  #hideHint() {
    clearTimeout(this.#hintTimeout);
    this.#hintTimeout = null;
    this.#hintPopup?.remove();
    this.#hintPopup = null;
  }

  /**
   * Renders the regions: a tinted fill that is the hover/click target in the overview stage, name labels shown
   * only there, and an outline that stays on faintly while building so the user can see which neighborhood they are
   * in. Which of those shows is #syncStage's call.
   */
  #renderRegionsHelper() {
    const map = this.#map;
    // Precompute each region's tint as a property — plain data beats a computed style expression here.
    this.#regionData.features.forEach((feature) => {
      const colors = RouteBuilder.REGION_FILL_COLORS;
      feature.properties.fill_color = colors[Math.abs(feature.properties.region_id) % colors.length];
    });
    map.addSource('regions', {
      type: 'geojson',
      data: this.#regionData,
      promoteId: 'region_id',
    });
    map.addLayer({
      id: 'regions-fill',
      type: 'fill',
      source: 'regions',
      paint: {
        'fill-color': ['get', 'fill_color'],
        'fill-opacity': RouteBuilder.REGION_PAINT_OVERVIEW.fillOpacity,
      },
    });
    map.addLayer({
      id: 'regions-outline',
      type: 'line',
      source: 'regions',
      paint: {
        'line-color': RouteBuilder.#token('--color-asphalt-500'),
        'line-width': 1.5,
        'line-opacity': RouteBuilder.REGION_PAINT_OVERVIEW.lineOpacity,
      },
    });
    map.addLayer({
      id: 'regions-label',
      type: 'symbol',
      source: 'regions',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15],
      },
      paint: {
        'text-color': RouteBuilder.#token('--color-asphalt-500'),
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 1.5,
      },
    });

    // In the overview, where a region is what a click acts on, regions highlight on hover.
    map.on('mousemove', 'regions-fill', (event) => {
      if (!this.#inOverview()) return;
      const regionId = event.features[0].properties.region_id;
      if (regionId !== this.#hoveredRegion) {
        this.#clearRegionHover();
        this.#hoveredRegion = regionId;
        map.setFeatureState({ source: 'regions', id: regionId }, { hover: true });
      }
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'regions-fill', () => {
      this.#clearRegionHover();
      map.getCanvas().style.cursor = '';
    });

    this.#status.regionsRendered = true;
    this.#syncStage();
    this.#maybeRestorePendingRoute();
  }

  /**
   * Zooms from the overview in to a clicked region, far enough that the next click can place a point.
   *
   * A region that fits on screen at placing zoom is framed whole. A bigger one can't be — framing it would leave the
   * user still in the overview, having clicked and got nowhere — so the map goes to the spot they clicked instead.
   *
   * @param {number} regionId
   * @param {{lng: number, lat: number}} lngLat - Where in the region the user clicked.
   */
  #zoomToRegion(regionId, lngLat) {
    const map = this.#map;
    const region = this.#regionData?.features.find((n) => n.properties.region_id === regionId);
    if (!region) return;
    // Mousemove doesn't fire during the zoom animation, so drop the stale pointer feedback now.
    this.#setCursorGuide(null);
    const framed = map.cameraForBounds(turf.bbox(region), { padding: 60, maxZoom: 16 });
    if (framed && framed.zoom >= RouteBuilder.PLACE_ZOOM) {
      map.easeTo({ center: framed.center, zoom: framed.zoom, duration: 1200 });
    } else {
      map.easeTo({ center: [lngLat.lng, lngLat.lat], zoom: RouteBuilder.PLACE_ZOOM, duration: 1200 });
    }
  }

  /**
   * @param {GeoJSON.FeatureCollection} regionDataIn - GeoJSON of the city's regions.
   * @returns {Promise<void>} Settles once the regions are drawn; rejects if drawing them fails.
   */
  renderRegions(regionDataIn) {
    this.#regionData = regionDataIn;
    return this.#mapLoaded.then(() => this.#renderRegionsHelper());
  }

  /**
   * Renders the streets on the map and wires the routing interaction.
   *
   * Two sources: 'streets' holds every street in the city (a thin, quiet base network you click to route along);
   * 'streets-chosen' holds the drawn route (an arrow pattern showing walking direction). A street in the route has
   * its base copy hidden via the 'chosen' feature-state. Clicking the drawn route opens the reverse/delete menu;
   * clicking anywhere else extends the route to the nearest street. Before the route starts, a ghost dot previews
   * the intersection the first click would snap to; the endpoint flags render as a symbol layer (drawn in the same
   * WebGL pass as the route line, unlike DOM markers, so they can never drift off it).
   */
  #renderStreetsHelper() {
    const map = this.#map;
    map.addSource('streets', {
      type: 'geojson',
      data: this.#streetData,
      promoteId: 'street_edge_id',
    });
    this.#streetsInRoute = { type: 'FeatureCollection', features: [] };
    map.addSource('streets-chosen', {
      type: 'geojson',
      data: this.#streetsInRoute,
      promoteId: 'street_edge_id',
    });
    // The ghost start dot needs snapping before any click, so build the routing graph right away.
    this.#getRouteGraph();

    map.addLayer({
      id: 'streets',
      type: 'line',
      source: 'streets',
      paint: {
        // Quiet neutral base network; the ghost flag (not a street highlight) previews where a click lands.
        'line-color': RouteBuilder.#token('--color-neutral-500'),
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 1, 15, 4],
        // Hidden where the street is part of the drawn route (that copy renders in 'streets-chosen' instead).
        'line-opacity': ['case', ['boolean', ['feature-state', 'chosen'], false], 0.0, 0.45],
      },
    });
    map.addLayer({
      id: 'streets-chosen',
      type: 'line',
      source: 'streets-chosen',
      paint: {
        'line-pattern': 'street-arrow',
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 15, 8],
        'line-opacity': 0.9,
      },
    });

    // Flag + explorer icons render as symbol layers (drawn in the same WebGL pass as the route line, unlike DOM
    // markers, so they can never drift off it). The SVGs/PNG are rasterized ourselves because Mapbox's loadImage
    // only decodes raster formats and this GL build's callback API doesn't return a promise.
    Promise.all([
      RouteBuilder.#rasterizeIcon(util.assetPath('images/icons/routebuilder/flag-start.svg'), 27),
      RouteBuilder.#rasterizeIcon(util.assetPath('images/icons/routebuilder/flag-end.svg'), 27),
      RouteBuilder.#rasterizeIcon(util.assetPath('images/icons/project_sidewalk_flag.png'), 40),
    ]).then(([startFlag, endFlag, explorer]) => {
      map.addImage('routebuilder-start-flag', startFlag.data, { pixelRatio: startFlag.pixelRatio });
      map.addImage('routebuilder-end-flag', endFlag.data, { pixelRatio: endFlag.pixelRatio });
      map.addImage('routebuilder-explorer', explorer.data, { pixelRatio: explorer.pixelRatio });

      // Ghost flag: a translucent preview of exactly where the next click lands — the start flag before the route
      // begins, the end flag while extending it.
      map.addSource('ghost-start', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'ghost-start',
        type: 'symbol',
        source: 'ghost-start',
        layout: {
          'icon-image': ['concat', 'routebuilder-', ['get', 'kind'], '-flag'],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
        },
        paint: {
          'icon-opacity': 0.55,
        },
      });

      map.addSource('route-endpoints', { type: 'geojson', data: this.#endpointData });
      map.addLayer({
        id: 'route-endpoints',
        type: 'symbol',
        source: 'route-endpoints',
        layout: {
          'icon-image': ['concat', 'routebuilder-', ['get', 'kind'], '-flag'],
          'icon-anchor': 'bottom', // The flag pole is planted on the route point; the label hangs below it.
          'icon-allow-overlap': true,
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 13,
          'text-anchor': 'top',
          'text-offset': [0, 0.4],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': RouteBuilder.#token('--color-asphalt-500'),
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1.5,
        },
      });

      // The explorer that walks the route in the save-hover preview animation.
      map.addSource('route-explorer', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'route-explorer',
        type: 'symbol',
        source: 'route-explorer',
        layout: {
          'icon-image': 'routebuilder-explorer',
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
    }).catch((e) => console.error('Failed to prepare map icons:', e));

    // Resting the pointer on the drawn route opens its action menu (so it's discoverable without knowing to
    // click); clicking opens it immediately, with keyboard focus.
    let routeHoverTimer = null;
    map.on('mousemove', 'streets-chosen', (event) => {
      // The map keeps drawing a cleared route for a frame or two, and a route has no menu once it's gone.
      if (!this.#routeDrawn()) return;
      map.getCanvas().style.cursor = 'pointer';
      this.#routePopover.cancelHoverClose();
      if (this.#routePopover.isOpen()) return;
      clearTimeout(routeHoverTimer);
      const lngLat = event.lngLat;
      routeHoverTimer = setTimeout(() => {
        // The route may have been cleared, or undone back to its start, while the pointer rested on it.
        if (this.#routePopover.isOpen() || !this.#routeDrawn()) return;
        window.logWebpageActivity('RouteBuilder_Hover=RouteMenu_Open');
        this.#routePopover.open(lngLat);
      }, RouteBuilder.ROUTE_MENU_HOVER_MS);
    });
    map.on('mouseleave', 'streets-chosen', () => {
      clearTimeout(routeHoverTimer);
      map.getCanvas().style.cursor = '';
      this.#routePopover.scheduleHoverClose();
    });
    map.on('mousemove', (event) => this.#onMapPointerMove(event.lngLat));
    map.on('mouseout', () => {
      this.#clearGhostStart();
      this.#setCursorGuide(null);
    });

    // Click handling follows the staged flow. On the drawn route: open the reverse/delete menu (a small pixel box
    // around the click gives the thin line a comfortable hit target). In the overview: zoom in to the region
    // under the click. Otherwise the click is a point — the start, then each extension of the route.
    map.on('click', (event) => {
      const { x, y } = event.point;
      const onRoute = this.#routeDrawn()
        && map.queryRenderedFeatures([[x - 6, y - 6], [x + 6, y + 6]], { layers: ['streets-chosen'] }).length > 0;
      if (onRoute) {
        window.logWebpageActivity('RouteBuilder_Click=RouteMenu_Open');
        this.#routePopover.open(event.lngLat, true);
        return;
      }
      if (this.#inOverview()) {
        const clickedRegionId = this.#regionIdAtPoint(event.point);
        if (clickedRegionId === null) return; // Clicked outside every region.
        window.logWebpageActivity(`RouteBuilder_Click=ZoomToRegion_RegionId=${clickedRegionId}`);
        this.#zoomToRegion(clickedRegionId, event.lngLat);
        return;
      }
      this.#addWaypoint(event.lngLat, 'MapClick');
    });

    this.#status.streetsRendered = true;
    this.#maybeRestorePendingRoute();
  }

  /**
   * Returns the region id of the region polygon under a screen point, or null if there is none.
   *
   * @param {{x: number, y: number}} point - Screen position of a map event.
   * @returns {number|null}
   */
  #regionIdAtPoint(point) {
    if (!this.#map.getLayer('regions-fill')) return null;
    const features = this.#map.queryRenderedFeatures(point, { layers: ['regions-fill'] });
    return features.length > 0 ? features[0].properties.region_id : null;
  }

  /**
   * Pointer pipeline for the map (rAF-throttled — snapping scans the street network, so at most one pass per
   * frame): keeps the ghost flag (where the next click lands) and the cursor guide (what the next click does) in
   * sync with the mouse.
   *
   * @param {{lng: number, lat: number}} lngLat - The mouse position.
   */
  #onMapPointerMove(lngLat) {
    this.#ghostLngLat = lngLat;
    if (this.#ghostRaf !== null) return;
    this.#ghostRaf = requestAnimationFrame(() => {
      this.#ghostRaf = null;
      const point = this.#map.project(this.#ghostLngLat);
      const snapped = this.#updateGhost();
      this.#updateCursorGuide(this.#regionIdAtPoint(point), snapped, point);
    });
  }

  /**
   * Moves the ghost flag to the intersection nearest the mouse. Past the overview this is the click affordance: a
   * translucent start flag before the first point, then a translucent end flag while extending. It shows only where
   * a click would actually land a point, so it is held to the same snap distance #addWaypoint enforces.
   *
   * @returns {boolean} Whether the flag is showing, i.e. whether a click here would place a point.
   */
  #updateGhost() {
    const source = this.#map.getSource('ghost-start');
    if (!source || !this.#routeGraph || this.#inOverview()) return false;
    const snap = this.#routeGraph.snapToStreet(this.#ghostLngLat, RouteBuilder.MAX_SNAP_DISTANCE_M);
    if (!snap) {
      this.#clearGhostStart();
      return false;
    }
    const kind = this.#routeStarted() ? 'end' : 'start';
    source.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature', properties: { kind }, geometry: { type: 'Point', coordinates: snap.nodeLngLat },
      }],
    });
    return true;
  }

  /**
   * The cursor guide: a small bubble following the pointer that says what a click here does — zoom in to a region
   * (named), start the route, or set the end point. It retires once the mechanic is demonstrably learned (2+
   * points) and stays out of the way of the drawn route's own menu.
   *
   * @param {number|null} hoverRegionId - Region under the pointer, if any.
   * @param {boolean} snapped - Whether the ghost flag found a street here, so a click would place a point.
   * @param {{x: number, y: number}} point - Screen position of the pointer.
   */
  #updateCursorGuide(hoverRegionId, snapped, point) {
    let text = null;
    const overRoute = this.#routeDrawn()
      && this.#map.queryRenderedFeatures([[point.x - 6, point.y - 6], [point.x + 6, point.y + 6]],
        { layers: ['streets-chosen'] }).length > 0;
    if (!overRoute && !this.#routePopover.isOpen()) {
      if (this.#inOverview()) {
        if (hoverRegionId !== null) {
          text = i18next.t('guide-select-region', { region: this.#getRegionName(hoverRegionId) ?? '' });
        }
      } else if (snapped && !this.#routeStarted()) {
        text = i18next.t('guide-pick-start');
      } else if (snapped && this.#waypoints.length === 1) {
        text = i18next.t('guide-pick-end');
      }
    }
    this.#setCursorGuide(text);
  }

  /**
   * Shows the pointer-following guide bubble with the given text, or hides it when null.
   * @param {string|null} text
   */
  #setCursorGuide(text) {
    if (text === null) {
      this.#cursorGuide?.remove();
      return;
    }
    if (!this.#cursorGuide) {
      this.#cursorGuide = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: '260px',
        offset: 30, // Large enough that the bubble clears the ghost flag sitting at the snapped intersection.
        className: 'rb-cursor-guide',
      }).trackPointer();
    }
    this.#cursorGuide.setText(text);
    if (!this.#cursorGuide.isOpen()) this.#cursorGuide.addTo(this.#map);
  }

  /** Hides the ghost flag (mouse left the map, or the click just landed). */
  #clearGhostStart() {
    this.#map.getSource('ghost-start')?.setData({ type: 'FeatureCollection', features: [] });
  }

  /**
   * @param {GeoJSON.FeatureCollection} streetDataIn - GeoJSON of the city's streets.
   * @returns {Promise<void>} Settles once the streets are drawn; rejects if drawing them fails.
   */
  renderStreets(streetDataIn) {
    this.#streetData = streetDataIn;
    return this.#mapLoaded.then(() => this.#renderStreetsHelper());
  }

  /**
   * Builds (once) and returns the street-network graph used for routing.
   */
  #getRouteGraph() {
    if (!this.#routeGraph) this.#routeGraph = new RouteGraph(this.#streetData.features);
    return this.#routeGraph;
  }

  /**
   * Adds a waypoint at the nearest street to a clicked/typed point and extends the route to it. A non-first
   * waypoint must be reachable from the previous one along the street network; which regions the path runs through
   * on the way is of no concern here (#3488).
   *
   * @param {{lng: number, lat: number}} lngLat - The click or geocoded address.
   * @param {string} source - Where the point came from, for activity logging ('MapClick'/'AddressStart'/...).
   */
  #addWaypoint(lngLat, source) {
    if (!this.#status.regionsRendered || this.#streetData === null || this.#streetsInRoute === null) return;
    const graph = this.#getRouteGraph();

    // Capped: an address the geocoder places past the edge of the street network would otherwise snap to whatever
    // street happens to be nearest, silently extending the route to somewhere the user never pointed at.
    const snap = graph.snapToStreet(lngLat, RouteBuilder.MAX_SNAP_DISTANCE_M);
    if (!snap) {
      this.#showMapMessage(i18next.t('no-street-nearby'));
      window.logWebpageActivity(`RouteBuilder_AddWaypoint=NoStreetNearby_Source=${source}`);
      return;
    }
    const point = { lng: snap.nodeLngLat[0], lat: snap.nodeLngLat[1] };
    // A non-first point must be reachable from the current end along the street network. The leg this finds is
    // the leg we keep — re-deriving it during the redraw would be both wasted work and a chance to diverge.
    let newSegment = null;
    if (this.#waypoints.length > 0) {
      const result = graph.route(this.#waypoints[this.#waypoints.length - 1], point);
      if (result.error) {
        this.#showMapMessage(i18next.t('no-path-error'));
        window.logWebpageActivity(`RouteBuilder_AddWaypoint=NoPath_Source=${source}`);
        return;
      }
      newSegment = result.streets;
    }

    this.#waypoints.push(point);
    if (newSegment !== null) this.#segments.push(newSegment);
    this.#undoStack.push({ type: 'waypoint' });
    if (this.#waypoints.length === 1) {
      this.#setPanelState('building');
      this.#clearGhostStart();
      // Ease in close enough that street names are legible, so the user can keep building from the start point.
      if (this.#map.getZoom() < RouteBuilder.BUILD_ZOOM) {
        this.#map.easeTo({ center: [point.lng, point.lat], zoom: RouteBuilder.BUILD_ZOOM, duration: 1200 });
      }
    }
    this.#recompute();
    this.#setCursorGuide(null); // Stale until the next pointer move re-evaluates it for the new stage.
    // The one static hint: right after the end point lands, reassure that the route isn't capped at two clicks.
    if (this.#waypoints.length === 2) {
      this.#showHint([point.lng, point.lat], i18next.t('hint-extend'));
    }
    window.logWebpageActivity(`RouteBuilder_AddWaypoint=Success_Count=${this.#waypoints.length}_Source=${source}`);
  }

  /**
   * Redraws every derived piece of state (drawn streets, endpoint flags, stats, direction-panel fields, CTA) from
   * #segments. The street features are cloned from the source data and oriented to the walking direction, so the
   * originals are never mutated.
   *
   * This does no routing. Each leg is resolved once, when it is created (#addWaypoint) or read back from a saved
   * route, and then carried verbatim — so what is drawn is always exactly the street list that will be saved.
   * Re-routing here instead would mean a saved route's own streets are only ever a suggestion: a street the graph
   * can't represent would silently vanish, and a parallel street sharing both endpoints could silently take
   * another's place, on every load and every subsequent edit.
   */
  #recompute() {
    const map = this.#map;
    this.#hideHint(); // Any old next-step hint is anchored to a point that may just have moved or vanished.
    this.#clearChosenStates();

    const features = [];
    this.#segments.flat().forEach(({ streetId, flip }) => {
      const orig = this.#getRouteGraph().getFeature(streetId);
      if (!orig) return;
      const coords = orig.geometry.coordinates.slice(); // Shallow copy: reversing it never touches the original.
      if (flip) coords.reverse();
      features.push({
        type: 'Feature',
        properties: { street_edge_id: streetId, region_id: orig.properties.region_id, reverse: flip },
        geometry: { type: 'LineString', coordinates: coords },
      });
      map.setFeatureState({ source: 'streets', id: streetId }, { chosen: true });
      this.#chosenIds.add(streetId);
    });
    this.#streetsInRoute.features = features;
    map.getSource('streets-chosen').setData(this.#streetsInRoute);

    this.#updateEndpointFlags();
    this.#updateStats();
    this.#syncDirectionsFields();
    this.#directionsPanel.setEndVisible(this.#routeStarted());
    this.#syncStage(); // A first point leaves the overview (and clearing the route may return to it) at any zoom.
    this.#updateCta();
  }

  /**
   * Plants the route's first point from an address typed into the Start field.
   *
   * Every waypoint is appended to the route's tail, so once a route exists there is no such thing as "setting the
   * start" — feeding the typed point through would extend the route to it and flag it as the finish, the exact
   * opposite of what the field says it does. Rather than silently reinterpret it, say the route has to be cleared.
   *
   * @param {{lng: number, lat: number}} lngLat - The geocoded address.
   */
  #setStartFromAddress(lngLat) {
    if (this.#waypoints.length > 0) {
      this.#showMapMessage(i18next.t('start-locked-warning'));
      this.#syncDirectionsFields(); // Put the route's real start back in the field the user just overwrote.
      window.logWebpageActivity('RouteBuilder_AddWaypoint=StartLocked_Source=AddressStart');
      return;
    }
    this.#addWaypoint(lngLat, 'AddressStart');
  }

  /**
   * Clears the 'chosen' feature-state of every currently drawn street, so base streets hidden underneath the
   * route reappear once they leave it.
   */
  #clearChosenStates() {
    this.#chosenIds.forEach((id) => this.#map.setFeatureState({ source: 'streets', id }, { chosen: false }));
    this.#chosenIds.clear();
  }

  /**
   * Mirrors the route's endpoints into the Start/End address fields (reverse-geocoded); a lone start fills just Start.
   * Only called while the route has a start: emptying it clears the fields through #resetUI.
   */
  #syncDirectionsFields() {
    const feats = this.#streetsInRoute.features;
    if (feats.length > 0) {
      const startCoord = feats[0].geometry.coordinates[0];
      const endCoord = feats[feats.length - 1].geometry.coordinates.slice(-1)[0];
      this.#directionsPanel.updateFromRoute(startCoord, endCoord);
    } else {
      this.#directionsPanel.updateStart([this.#waypoints[0].lng, this.#waypoints[0].lat]);
    }
  }

  /**
   * Updates the stats block in the planner card: a headline of estimated exploration time (distance x the city's
   * labeling pace) and distance, with a street-count + region caption below. Cleared when the route is empty.
   */
  #updateStats() {
    const feats = this.#streetsInRoute.features;
    // A lone start point isn't a route yet: no stats to show, nothing to save.
    const hasRoute = feats.length > 0;
    this.#routeStatsEl.hidden = !hasRoute;
    this.#updateSaveButton();
    if (!hasRoute) {
      this.#streetDistanceEl.innerText = '';
      this.#routeTimeEl.innerText = '';
      this.#routeTimeTipEl.textContent = '';
      this.#statsCaptionEl.textContent = '';
      return;
    }

    // RouteGraph measured every street once when it was built, so re-measuring the route's geometry on each
    // redraw would be repeating that work for the whole route on every click.
    const graph = this.#getRouteGraph();
    const km = this.#segments.flat().reduce((sum, { streetId }) => sum + graph.getLengthM(streetId), 0) / 1000;
    this.#streetDistanceEl.innerText = this.#formatDistance(km);
    const estTime = this.#formatEstTime(km);
    this.#routeTimeEl.innerText = estTime;
    // The tip spells out what the headline number is: time labeling in the tool, not time walking the route. It
    // names the route's own estimate rather than only the pace, since "N minutes per 100 m" alone read as vague.
    this.#routeTimeTipEl.textContent
      = i18next.t('est-time-tooltip', { time: estTime, pace: this.#minutesPer100m.toFixed(1) });

    const regionIds = this.#routeRegionIds();
    const regionName = this.#getRegionName(regionIds[0]);
    if (regionIds.length > 1) {
      this.#statsCaptionEl.textContent
        = i18next.t('street-count-across-regions', { streets: feats.length, regions: regionIds.length });
    } else {
      this.#statsCaptionEl.textContent = regionName
        ? i18next.t('street-count-in-region', { count: feats.length, region: regionName })
        : i18next.t('street-count', { count: feats.length });
    }
  }

  /**
   * The regions the drawn route runs through, in the order it first enters each — so the first is the one it
   * starts in, which is also the one the server files it under.
   *
   * @returns {number[]} Empty when no route is drawn.
   */
  #routeRegionIds() {
    return [...new Set((this.#streetsInRoute?.features ?? []).map((f) => f.properties.region_id))];
  }

  /**
   * Whether the loaded saved route has edits that haven't been written back to the database.
   * @returns {boolean}
   */
  #isDirty() {
    return this.#editingRouteId !== null
      && JSON.stringify(this.#routeStreetsPayload()) !== this.#savedBaseline;
  }

  /**
   * Keeps the save button's label and enabled state in sync with the editing state: "Save route" for a new
   * route, "Update route" for a loaded route with unsaved edits, and a disabled "Saved" once written back.
   * Also mirrors the editing state onto the planner card, which shows the saved-route cards while a saved
   * route is open so the just-saved/loaded route is visible in the panel.
   */
  #updateSaveButton() {
    this.#panel.dataset.editing = this.#editingRouteId !== null;
    const hasRoute = this.#routeDrawn();
    this.#previewButton.disabled = !hasRoute;
    if (this.#editingRouteId === null) {
      this.#saveButton.textContent = this.#defaultSaveLabel;
      this.#saveButton.disabled = !hasRoute;
    } else if (this.#isDirty()) {
      this.#saveButton.textContent = i18next.t('save-update');
      this.#saveButton.disabled = !hasRoute;
    } else {
      this.#saveButton.textContent = i18next.t('save-saved');
      this.#saveButton.disabled = true;
    }
  }

  /**
   * Formats a route length in the user's display units, e.g. "0.48 mi".
   * @param {number} km
   * @returns {string}
   */
  #formatDistance(km) {
    return util.longDistanceToString(km, 2);
  }

  /**
   * Formats the estimated exploration time for a route length, e.g. "~31 min" or "~1 hr 5 min".
   * @param {number} km
   * @returns {string}
   */
  #formatEstTime(km) {
    const minutes = Math.max(1, Math.round((km * 1000 / 100) * this.#minutesPer100m));
    return minutes < 60
      ? i18next.t('est-time-minutes', { min: minutes })
      : i18next.t('est-time-hours', { hours: Math.floor(minutes / 60), min: minutes % 60 });
  }

  /**
   * One-line meta description for a saved-route card: distance, estimated exploration time, and region.
   *
   * @param {number} distanceMeters
   * @param {string|null} regionName
   * @returns {string}
   */
  #formatRouteMeta(distanceMeters, regionName) {
    const km = distanceMeters / 1000;
    return [this.#formatDistance(km), this.#formatEstTime(km), regionName].filter(Boolean).join(' · ');
  }

  /**
   * Rebuilds the 'route-endpoints' symbol source from the current route (or a lone start point): the start and end
   * flags, each with a Start/End text label below the point.
   */
  #updateEndpointFlags() {
    const features = [];
    const feats = this.#streetsInRoute.features;
    if (feats.length > 0) {
      const endCoord = feats[feats.length - 1].geometry.coordinates.slice(-1)[0];
      features.push(RouteBuilder.#endpointFeature('start', feats[0].geometry.coordinates[0]));
      features.push(RouteBuilder.#endpointFeature('end', endCoord));
    } else if (this.#waypoints.length === 1) {
      // A start has been dropped but no segment exists yet: show a provisional start flag.
      const wp = this.#waypoints[0];
      features.push(RouteBuilder.#endpointFeature('start', [wp.lng, wp.lat]));
    }
    this.#endpointData = { type: 'FeatureCollection', features };
    // The source may not exist yet (icons rasterize async); it is then created with #endpointData as its data.
    this.#map.getSource('route-endpoints')?.setData(this.#endpointData);
  }

  /**
   * Builds one endpoint feature for the 'route-endpoints' symbol source.
   *
   * @param {string} kind - 'start' or 'end'; selects the flag image via the layer's icon-image expression.
   * @param {Array<number>} coord - [lng, lat] of the endpoint.
   * @returns {object} A GeoJSON Point feature.
   */
  static #endpointFeature(kind, coord) {
    return {
      type: 'Feature',
      properties: { kind, label: i18next.t(`marker-${kind}`) },
      geometry: { type: 'Point', coordinates: coord },
    };
  }

  /**
   * Rasterizes an icon file for use as a map symbol image (map.addImage needs pixel data, and Mapbox's loadImage
   * can't decode SVGs). Rendered at 2x for crisp display on high-DPI screens, preserving aspect ratio.
   *
   * @param {string} url - Same-origin image url (SVG or raster).
   * @param {number} heightPx - Displayed height in CSS pixels; width follows the image's aspect ratio.
   * @returns {Promise<{data: ImageData, pixelRatio: number}>}
   */
  static async #rasterizeIcon(url, heightPx) {
    const img = new Image();
    img.src = url;
    await img.decode();
    const h = heightPx * 2;
    const w = Math.round((img.naturalWidth / img.naturalHeight) * h);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    return { data: ctx.getImageData(0, 0, w, h), pixelRatio: 2 };
  }

  /**
   * Walks the explorer along the route from start to end — a playful preview, triggered by the Preview button.
   * One run at a time; the icon lingers at the end for a beat, then disappears.
   */
  #animateExplorer() {
    const feats = this.#streetsInRoute.features;
    const source = this.#map.getSource('route-explorer');
    if (this.#explorerRaf !== null || feats.length === 0 || !source) return;

    // Cumulative distance along the route's coordinates, for constant-speed interpolation.
    const coords = feats.flatMap((f) => f.geometry.coordinates);
    const cumDist = [0];
    for (let i = 1; i < coords.length; i++) {
      cumDist.push(cumDist[i - 1] + turf.distance(coords[i - 1], coords[i], { units: 'meters' }));
    }
    const totalDist = cumDist[cumDist.length - 1];
    if (totalDist === 0) return;

    let startTime = null;
    const step = (now) => {
      if (startTime === null) startTime = now;
      const t = Math.min((now - startTime) / RouteBuilder.EXPLORER_ANIMATION_MS, 1);
      const target = t * totalDist;
      let i = 1;
      while (i < cumDist.length - 1 && cumDist[i] < target) i++;
      const segT = (target - cumDist[i - 1]) / (cumDist[i] - cumDist[i - 1] || 1);
      const lng = coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * segT;
      const lat = coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * segT;
      source.setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [lng, lat] } }],
      });
      if (t < 1) {
        this.#explorerRaf = requestAnimationFrame(step);
      } else {
        this.#explorerRaf = null;
        setTimeout(() => {
          // Skip the cleanup if a new run started during the linger.
          if (this.#explorerRaf === null) source.setData({ type: 'FeatureCollection', features: [] });
        }, 500);
      }
    };
    this.#explorerRaf = requestAnimationFrame(step);
  }

  /** Stops the explorer animation and removes the icon (the route is being cleared or replaced). */
  #cancelExplorer() {
    if (this.#explorerRaf !== null) {
      cancelAnimationFrame(this.#explorerRaf);
      this.#explorerRaf = null;
    }
    this.#map.getSource('route-explorer')?.setData({ type: 'FeatureCollection', features: [] });
  }

  /**
   * Reverses the whole route's walking direction (swaps start and end).
   *
   * The existing streets are reversed in place rather than re-routed, so a reversal can't quietly pick a
   * different path back, and undoing one is exact.
   */
  #reverseRoute() {
    if (this.#waypoints.length < 2) return;
    this.#applyReverse();
    this.#undoStack.push({ type: 'reverse' });
    this.#recompute();
  }

  /** Flips the route end-to-end: waypoint order, leg order, and each leg's street order and direction. */
  #applyReverse() {
    this.#waypoints.reverse();
    this.#segments.reverse();
    this.#segments = this.#segments.map(
      (segment) => segment.slice().reverse().map(({ streetId, flip }) => ({ streetId, flip: !flip })),
    );
  }

  /**
   * Undoes the last edit: either the most recently added waypoint (and its leg) or a reversal. Empties the route
   * once the start is removed.
   */
  #undo() {
    const action = this.#undoStack.pop();
    if (action === null) return;
    if (action.type === 'reverse') {
      this.#applyReverse();
      this.#recompute();
      return;
    }
    if (this.#waypoints.length === 0) return;
    this.#waypoints.pop();
    this.#segments.pop();
    if (this.#waypoints.length === 0) {
      this.#emptyRoute();
      this.#resetUI();
    } else {
      this.#recompute();
    }
  }

  /**
   * Clears #waypoints and all derived route state (drawn streets, flags). Does not touch the undo stack or the
   * panel — callers decide those.
   */
  #emptyRoute() {
    const map = this.#map;
    this.#clearChosenStates();
    this.#waypoints = [];
    this.#segments = [];
    this.#streetsInRoute.features = [];
    map.getSource('streets-chosen').setData(this.#streetsInRoute);
    this.#updateEndpointFlags();
    this.#routePopover.close();
    this.#cancelExplorer();
    this.#hideHint();
    this.#syncStage(); // With no route left, a zoomed-out map is back in the overview.
    this.#editingRouteId = null;
    this.#savedBaseline = null;
    this.#updateStats();
    this.#savedRoutes.markActive(null);
  }

  /**
   * Restores a route stashed in sessionStorage before a sign-in reload, then reopens the save modal. With no
   * stash, opens the saved route a ?preview= deep link names, if any.
   *
   * Runs once, after the map, regions, and streets have all *rendered* — the streets-rendered gate matters:
   * drawing the route needs the 'streets-chosen' source, which doesn't exist while the street GeoJSON has merely
   * arrived.
   */
  #maybeRestorePendingRoute() {
    if (this.#status.pendingRouteRestored
      || !this.#status.regionsRendered || !this.#status.streetsRendered) return;

    this.#status.pendingRouteRestored = true;
    const pending = SaveModal.consumePendingRoute();
    if (!pending) {
      // Deep link from the dashboard's route cards: /routeBuilder?preview=<id> opens the route in the editor.
      const previewParam = new URLSearchParams(window.location.search).get('preview');
      if (previewParam !== null && /^\d+$/.test(previewParam)) this.#loadRouteForEditing(Number(previewParam));
      return;
    }

    if (this.#seedRouteFromStreets(pending.streets) === 0) return;
    this.#setPanelState('building');
    this.#applyCamera(pending.camera); // Back to the exact view the user left for the sign-in round trip.
    this.#saveModal.open(pending.name || null, pending.description || null);
  }

  /**
   * Adopts a stored street list as the current route: each street becomes its own leg, carried verbatim, with a
   * waypoint at every street boundary so further editing extends it exactly like a route clicked out by hand.
   *
   * The stored ids are trusted rather than re-routed between the endpoints. Re-routing loses information the
   * stored list has and the graph doesn't: a street whose endpoints merge into a single node has no edge at all
   * and would simply disappear, and where two streets connect the same pair of intersections the router can only
   * pick one, silently swapping the other out. Either way the damage is invisible and the next update writes it
   * back over the real route.
   *
   * @param {Array<{street_id: number, reverse: boolean}>} streets - Ordered streets in the /saveRoute wire format.
   * @returns {number} How many of the streets resolved against the loaded street data (0 = nothing to draw).
   */
  #seedRouteFromStreets(streets) {
    const graph = this.#getRouteGraph();
    const resolved = streets
      .map((stored) => ({ streetId: stored.street_id, flip: stored.reverse === true }))
      .filter(({ streetId }) => graph.getFeature(streetId) !== undefined);
    if (resolved.length === 0) return 0;

    this.#segments = resolved.map((street) => [street]);
    this.#waypoints = this.#waypointsFromSegments();
    this.#recompute();
    return resolved.length;
  }

  /**
   * The waypoint chain for the current legs: the first street's start, then the end of each leg in walking order.
   * Keeps the waypoints.length === segments.length + 1 invariant the editing actions rely on.
   *
   * @returns {Array<{lng: number, lat: number}>}
   */
  #waypointsFromSegments() {
    if (this.#segments.length === 0) return [];
    const legEnds = this.#segments.map((segment) => {
      const last = segment[segment.length - 1];
      const coords = this.#orientedCoords(last);
      return coords[coords.length - 1];
    });
    const start = this.#orientedCoords(this.#segments[0][0])[0];
    return [start, ...legEnds].map(([lng, lat]) => ({ lng, lat }));
  }

  /**
   * Whether any of a stored route's streets still exists in the loaded street data.
   *
   * @param {Array<{street_id: number}>} streets - Stored streets in the /saveRoute wire format.
   * @returns {boolean} False means there is no route left to draw.
   */
  #anyStreetResolves(streets) {
    return streets.some((stored) => this.#getRouteGraph().getFeature(stored.street_id) !== undefined);
  }

  /** A street's coordinates in the route's walking direction (a copy when flipped; never mutates the source). */
  #orientedCoords({ streetId, flip }) {
    const coords = this.#getRouteGraph().getFeature(streetId).geometry.coordinates;
    return flip ? coords.slice().reverse() : coords;
  }

  /**
   * Confirms with the user before an action that would discard unsaved work: edits to a loaded saved route, or a
   * drawn-but-never-saved route. No-ops (resolves true) when nothing would be lost.
   *
   * @returns {Promise<boolean>} True to proceed.
   */
  #unsavedWorkConfirmed() {
    if (!this.#hasUnsavedWork()) return Promise.resolve(true);
    return ConfirmDialog.confirm({
      message: i18next.t('unsaved-continue-confirm'),
      confirmText: i18next.t('common:continue'),
      cancelText: i18next.t('common:cancel'),
    });
  }

  /**
   * Whether there is work a discard would lose: edits to a loaded saved route, or a drawn-but-never-saved route.
   * @returns {boolean}
   */
  #hasUnsavedWork() {
    return this.#editingRouteId !== null
      ? this.#isDirty()
      : this.#routeDrawn();
  }

  /**
   * Saves for a leave prompt that asked to save first. A loaded route updates in place; a new one has no name
   * yet, so it goes through the save modal and this resolves only once that closes one way or the other.
   *
   * @returns {Promise<boolean>} Whether the route was saved.
   */
  #saveForLeaving() {
    if (this.#editingRouteId !== null) return this.#updateSavedRoute();
    return new Promise((resolve) => {
      this.#settleLeaveSave(false); // Only one prompt can be waiting; an older one is no longer being answered.
      this.#pendingLeaveSave = resolve;
      this.#saveModal.open();
    });
  }

  /**
   * Answers the leave prompt waiting on the save modal, if there is one.
   * @param {boolean} saved - Whether the route made it to the database.
   */
  #settleLeaveSave(saved) {
    const resolve = this.#pendingLeaveSave;
    this.#pendingLeaveSave = null;
    resolve?.(saved);
  }

  /** Closes the editing session (or clears a new route), back to the intro state. The saved route is untouched. */
  #exitEditSession() {
    this.#emptyRoute();
    this.#undoStack.clear();
    this.#resetUI();
  }

  /**
   * Loads a saved route into the editor: its streets are drawn, its waypoints reconstructed, and further edits
   * update the same route in place. Clicking the already-loaded route's card closes the session instead.
   *
   * @param {number} routeId
   */
  async #loadRouteForEditing(routeId) {
    if (this.#editingRouteId === routeId) {
      if (!(await this.#unsavedWorkConfirmed())) return;
      window.logWebpageActivity('RouteBuilder_Click=ExitEditSession');
      this.#exitEditSession();
      return;
    }
    if (!(await this.#unsavedWorkConfirmed())) return;
    // Cards stay clickable while a load is in flight, and nothing else serializes two of them: the confirm above
    // sees no drawn route yet, so it doesn't ask. Without a token the last response wins rather than the last
    // click, and a slow first load would wipe the route the user actually asked for and leave them editing it.
    const loadId = ++this.#loadSeq;
    fetch(`/userapi/routes/${routeId}/streets`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((data) => {
        if (!this.#status.streetsRendered || loadId !== this.#loadSeq) return;
        // Resolve the stored streets before touching anything: if none of them are in the loaded street data
        // (every street hidden, or the region closed, since the route was saved), the user keeps what they had
        // open and gets told, rather than silently losing it to a load that then draws nothing.
        if (!this.#anyStreetResolves(data.streets)) {
          this.#showMapMessage(i18next.t('route-load-empty'));
          return;
        }
        this.#emptyRoute(); // Also closes any previous editing session.
        this.#undoStack.clear();
        const drawn = this.#seedRouteFromStreets(data.streets);
        this.#setPanelState('building');
        // If streets were dropped (e.g. since hidden as low-quality), say so — the user is looking at, and will
        // save, the reduced route.
        if (drawn !== data.streets.length) {
          Toast.show({ message: i18next.t('route-adjusted'), duration: 4000 });
        }
        this.#editingRouteId = routeId;
        // The baseline is what's actually drawn, so an untouched load reads as clean.
        this.#savedBaseline = JSON.stringify(this.#routeStreetsPayload());
        this.#savedRoutes.markActive(routeId);
        this.#updateSaveButton();
        const coords = this.#streetsInRoute.features.flatMap((f) => f.geometry.coordinates);
        this.#map.fitBounds(turf.bbox({ type: 'MultiPoint', coordinates: coords }),
          { padding: 80, maxZoom: 16, duration: 900 });
      })
      .catch((e) => {
        if (loadId !== this.#loadSeq) return;
        console.error('Failed to load the route for editing:', e);
        this.#showMapMessage(i18next.t('route-load-error'));
      });
  }

  /**
   * Writes the current street list back to the loaded saved route (PUT). The route keeps its id, slug, stats,
   * and share links; in-progress explorations reconcile server-side.
   *
   * @returns {Promise<boolean>} Whether the write succeeded.
   */
  #updateSavedRoute() {
    const payload = this.#routeStreetsPayload();
    const routeId = this.#editingRouteId;
    window.logWebpageActivity(`RouteBuilder_Click=UpdateRoute_RouteId=${routeId}`);
    return fetch(`/userapi/routes/${routeId}`, {
      method: 'PUT',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ streets: payload }),
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`${response.status}`))))
      .then((updated) => {
        this.#savedBaseline = JSON.stringify(payload);
        this.#updateSaveButton();
        // A guest's card list is localStorage, so refresh() alone would re-render the first save's numbers.
        if (!this.#isSignedIn) this.#recordGuestRoute(updated);
        this.#savedRoutes.refresh();
        Toast.show({ message: i18next.t('route-updated'), duration: 3000 });
        window.logWebpageActivity(`RouteBuilder_Click=UpdateSuccess_RouteId=${routeId}`);
        return true;
      })
      .catch((e) => {
        // A 404 means the route isn't ours anymore (e.g. a guest whose anonymous session rotated).
        const gone = e.message === '404' || e.message === '403';
        this.#showMapMessage(i18next.t(gone ? 'route-update-gone' : 'save-error'));
        window.logWebpageActivity('RouteBuilder_Click=UpdateError');
        return false;
      });
  }

  /**
   * The current map camera pose, in the shape mapbox-gl's jumpTo accepts (JSON-safe for the sign-in stash).
   * @returns {{center: Array<number>, zoom: number, bearing: number, pitch: number}}
   */
  #cameraSnapshot() {
    const center = this.#map.getCenter();
    return {
      center: [center.lng, center.lat],
      zoom: this.#map.getZoom(),
      bearing: this.#map.getBearing(),
      pitch: this.#map.getPitch(),
    };
  }

  /**
   * Restores a camera pose captured by #cameraSnapshot, so the sign-in round trip puts the user back at the exact
   * view they left. An unusable pose leaves the camera where it is.
   *
   * @param {Record<string, any>} [camera] - A stashed #cameraSnapshot, possibly absent or corrupt.
   */
  #applyCamera(camera) {
    const usable = Array.isArray(camera?.center) && camera.center.length === 2
      && camera.center.every(Number.isFinite) && Number.isFinite(camera.zoom);
    if (!usable) return;
    this.#map.jumpTo({
      center: camera.center,
      zoom: camera.zoom,
      bearing: Number.isFinite(camera.bearing) ? camera.bearing : 0,
      pitch: Number.isFinite(camera.pitch) ? camera.pitch : 0,
    });
  }

  /**
   * The trash-can / route-menu delete action. For a loaded saved route with no unsaved edits there is nothing to
   * lose, so the session just closes; otherwise the confirmation modal opens, with its copy swapped while editing
   * (discarding edits leaves the saved route intact — the server-rendered copy is about losing an unsaved route).
   */
  #openDeleteConfirm() {
    const editing = this.#editingRouteId !== null;
    if (editing && !this.#isDirty()) {
      window.logWebpageActivity('RouteBuilder_Click=ExitEditSession');
      this.#exitEditSession();
      return;
    }
    this.#deleteTitleEl.textContent = editing ? i18next.t('discard-edit-title') : this.#defaultDeleteCopy.title;
    this.#deleteExplanationEl.textContent = editing
      ? i18next.t('discard-edit-explanation')
      : this.#defaultDeleteCopy.explanation;
    this.#deleteConfirmButton.textContent = editing
      ? i18next.t('discard-edit-button')
      : this.#defaultDeleteCopy.button;
    this.#deleteRouteModal.style.visibility = 'visible';
  }

  #clickCancelRoute() {
    window.logWebpageActivity('RouteBuilder_Click=CancelRoute');
    this.#openDeleteConfirm();
  }

  #clickResumeRoute() {
    window.logWebpageActivity('RouteBuilder_Click=ResumeRoute');
    this.#deleteRouteModal.style.visibility = 'hidden';
  }

  /** Clears the current route and resets the map to the intro state. */
  #clearRoute() {
    this.#emptyRoute();
    this.#undoStack.clear();
    this.#resetUI();
    window.logWebpageActivity('RouteBuilder_Click=ConfirmCancelRoute');
  }

  #resetUI() {
    this.#setPanelState('empty');
    this.#deleteRouteModal.style.visibility = 'hidden';
    this.#saveModal.hide();
    this.#directionsPanel.clearFields();
    this.#directionsPanel.setEndVisible(false);
    this.#updateCta();
  }

  /**
   * Looks up a region's display name from the regions GeoJSON.
   *
   * @param {number} regionId
   * @returns {string|null} The region name, or null if unknown.
   */
  #getRegionName(regionId) {
    const region = this.#regionData?.features.find((n) => n.properties.region_id === regionId);
    return region ? region.properties.name : null;
  }

  /**
   * Returns the ordered street list for the route in the POST /saveRoute wire format. The route is already an
   * ordered, contiguous street sequence, so this is a direct map.
   * @returns {Array<{street_id: number, reverse: boolean}>}
   */
  #routeStreetsPayload() {
    return this.#streetsInRoute.features.map((s) => ({
      street_id: s.properties.street_edge_id,
      reverse: s.properties.reverse === true,
    }));
  }

  /**
   * After a successful first save: records the route (device-local list for guests), then keeps the route on the
   * map as an editing session — like saving a document, further edits update the same route via "Update route".
   * The new card is highlighted in "Your saved routes" and a toast confirms.
   *
   * @param {Record<string, any>} saved - /saveRoute's response: route_id, name, slug, distance_meters, thumbnail_url.
   */
  #handleRouteSaved(saved) {
    const routeId = saved.route_id;
    if (!this.#isSignedIn) this.#recordGuestRoute(saved);
    this.#editingRouteId = routeId;
    this.#savedBaseline = JSON.stringify(this.#routeStreetsPayload());
    this.#savedRoutes.markActive(routeId);
    this.#savedRoutes.refresh(routeId);
    this.#updateSaveButton();
    Toast.show({ message: i18next.t('route-saved'), duration: 3000 });
    this.#settleLeaveSave(true);
  }

  /**
   * Mirrors a just-saved route into the guest's device-local card list.
   *
   * The distance, geometry and thumbnail come from the server's response rather than being recomputed here, so a
   * guest card always describes what was actually stored — and an update refreshes it rather than leaving the
   * first save's numbers on the card forever.
   *
   * @param {Record<string, any>} saved - A /saveRoute or route-update response.
   */
  #recordGuestRoute(saved) {
    this.#savedRoutes.recordGuestRoute({
      routeId: saved.route_id,
      name: saved.name,
      slug: saved.slug,
      regionName: saved.region_name,
      regionCount: saved.region_count,
      url: `${window.location.origin}/r/${saved.slug}`,
      distanceMeters: saved.distance_meters,
      thumbnailUrl: saved.thumbnail_url,
    });
  }

  /**
   * Suggests a route name for the save modal from the reverse-geocoded start/end streets — "Palisade Ave to
   * Cedar Ln", or just the street name for a single-street loop. Falls back to "Route in {region}", naming the
   * region the route starts in, when the street names haven't resolved (geocoding is async/best-effort).
   *
   * @returns {string}
   */
  #suggestedRouteName() {
    const { start, end } = this.#directionsPanel.getEndpointStreetNames();
    if (start && end) {
      return start === end ? start : i18next.t('route-name-streets', { start, end });
    }
    const regionName = this.#getRegionName(this.#routeRegionIds()[0]);
    return regionName ? i18next.t('route-name-default', { region: regionName }) : '';
  }
}
